/**
 * Devices — Zod schemas.
 *
 * PureID treats devices as a weak auth signal: a row per
 * (tenant, user, user_agent) that gets bumped on every login.
 * No MDM integration, no OS version, no compliance state — the
 * backend deliberately avoids browser fingerprinting.
 *
 * If MDM integration lands later, extend both this schema and
 * the backend devices table; for now the shape reflects what's
 * actually in the column list.
 */

import { z } from "zod";

export const deviceTypeSchema = z.enum([
  "browser",
  "mobile",
  "desktop",
  "cli",
  "unknown",
]);
export type DeviceType = z.infer<typeof deviceTypeSchema>;

export const deviceStatusSchema = z.enum(["active", "revoked"]);
export type DeviceStatus = z.infer<typeof deviceStatusSchema>;

export const devicesListSearchSchema = z.object({
  q: z.string().optional(),
  deviceType: deviceTypeSchema.optional(),
  status: deviceStatusSchema.optional(),
  userId: z.string().optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
});
export type DevicesListSearch = z.infer<typeof devicesListSearchSchema>;

export const deviceSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  tenant_id: z.string(),
  device_type: deviceTypeSchema,
  user_agent: z.string().nullable(),
  friendly_name: z.string().nullable(),
  status: deviceStatusSchema,
  last_seen_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  revoked_at: z.string().nullable(),
});
export type Device = z.infer<typeof deviceSchema>;

export const devicesListResponseSchema = z.object({
  data: z.array(deviceSchema),
  pagination: z.object({
    total: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    // Uniform with every other list response — nonnegative, not
    // positive. See applications/schemas.ts for the rationale.
    limit: z.number().int().nonnegative(),
  }),
});
export type DevicesListResponse = z.infer<typeof devicesListResponseSchema>;
